import React from 'react';
import { GripVertical } from 'lucide-react';
import { RailResizeHandleBindings } from '../../hooks/useResizableRail';

/**
 * The drag edge on a right-docked rail: a thin strip straddling the rail's
 * left border, with a small grip pill at its middle. At rest the grip is a
 * quiet outline; on hover (or keyboard focus, or mid-drag) it lifts slightly
 * and the edge lights in action blue — resizing is the user's action, so it
 * is cobalt, not Robbie violet. Depth stays border-only, no shadow.
 *
 * Keyboard: ←/→ resize, Home/End jump to the limits. Double-click resets.
 * Place it inside a `relative` rail wrapper.
 */
export interface RailResizeHandleProps extends RailResizeHandleBindings {
  label: string;
}

export const RailResizeHandle: React.FC<RailResizeHandleProps> = ({
  label,
  active,
  valueMin,
  valueMax,
  valueNow,
  ...handlers
}) => (
  <div
    role="separator"
    aria-orientation="vertical"
    aria-label={label}
    aria-valuemin={valueMin}
    aria-valuemax={valueMax}
    aria-valuenow={valueNow}
    tabIndex={0}
    title="Drag to resize · double-click to reset"
    className="group absolute inset-y-0 left-0 z-10 w-[12px] -translate-x-1/2 cursor-col-resize touch-none select-none focus:outline-none"
    {...handlers}
  >
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute inset-y-0 left-1/2 w-[2px] -translate-x-1/2 transition-colors duration-150 motion-reduce:transition-none ${
        active ? 'bg-action' : 'bg-transparent group-hover:bg-action/40 group-focus-visible:bg-action'
      }`}
    />
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute left-1/2 top-1/2 flex h-[34px] w-[14px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-round border bg-surface transition-[transform,border-color,color] duration-150 ease-out motion-reduce:transition-none ${
        active
          ? 'scale-[1.15] border-action text-action'
          : 'border-line-strong text-ink-muted group-hover:scale-[1.15] group-hover:border-action group-hover:text-action group-focus-visible:scale-[1.15] group-focus-visible:border-action group-focus-visible:text-action'
      }`}
    >
      <GripVertical className="h-[12px] w-[12px]" />
    </span>
  </div>
);

export default RailResizeHandle;
